import { ISiteInfoUrl } from "../src/taskpane/interfaces/ISiteInfo";
import { decodeClientGroupRole, IClientGroupRole } from "../src/taskpane/services/clientGroupQuery";
import { SharePointSiteHelper } from "../utilities/SharePointSiteHelper";

// Where the client group sites of the site Advanced Search was opened from go in the site
// picker, and which of them are ticked with it. Pure, so it can be unit-tested on its own;
// the lookup itself is clientGroupService.resolveClientGroupView, run by advancedSearch.tsx.
//
// The placement is worked out on every open and never cached: a site is a group site only
// relative to the site search was opened from.

/**
 * Whether the task pane opened the Outlook window knowing the site is in no client group
 * (`roleParam`, from its session copy): then there is nothing to look up. Otherwise the window
 * finds the site's client groups the way the task pane does - the group rows saved on this
 * device, else the list read in full - including when the role is unknown (the copy was not in
 * yet, or the window was opened from a pinned or recent location).
 */
export const isKnownOutsideClientGroups = (roleParam: string | null | undefined): boolean => {
  const role: IClientGroupRole | undefined = decodeClientGroupRole(roleParam);
  return !!role && !role.isGroup && !role.isMember;
};

/** The header of the site's own members; each group it belongs to is keyed `${key}:<itemId>`. */
export const CLIENT_GROUP_SECTION_KEY: string = "clientGroupHeader";

/** Group sites are ticked with the site search was opened from only up to this many; a larger
 * group is listed for the user to tick instead. A ticked group site costs cached schema reads
 * and one search call per library: its libraries load no views (librariesOutsideGroupSites), its
 * users are read only when a people picker is used (advancedSearchPeople), and every per-library
 * read and search runs a few at a time (advancedSearchRequests). */
export const MAX_TICKED_GROUP_SITES: number = 20;

/** A site as clientGroupService returns it (IClientGroupSite), structurally. */
export interface IAdvancedSearchGroupSite {
  id: string;
  displayName: string;
  webUrl: string;
}

/** The service's client group view (IClientGroupView), structurally. */
export interface IAdvancedSearchGroupView {
  members: IAdvancedSearchGroupSite[];
  groups: { itemId: number; label: string; site?: IAdvancedSearchGroupSite; members: IAdvancedSearchGroupSite[] }[];
}

export interface IClientGroupSiteSection {
  key: string;
  text: string;
  sites: ISiteInfoUrl[];
}

export interface IClientGroupSitePlacement {
  /** The list, with every group site it did not hold appended as a top-level site. */
  sites: ISiteInfoUrl[];
  /** The own members first, then each group in the order the service gave them. */
  sections: IClientGroupSiteSection[];
  /** The group site ids to tick with the site search was opened from; empty above the bound. */
  tickIds: string[];
  /** Every group site in play, in a section or left under Subsites, whatever the bound. */
  groupSiteIds: string[];
  /** The group sites that were appended to the list. */
  added: ISiteInfoUrl[];
}

const idKey = (id: string | undefined): string => (id ?? "").trim().toLowerCase();

/**
 * Each group site is placed once, in the first section that names it (a picker option cannot
 * be listed twice). The site search was opened from is never placed, nor any other web of its
 * site collection: a client row belongs to the collection. A site the list already holds keeps
 * its entry: one under Sites or Teams moves into its group section, one under Subsites
 * (a subsite, channel or hub site) stays where it is, the same rule the locations grid applies.
 */
export const placeClientGroupSites = (
  sites: ISiteInfoUrl[],
  view: IAdvancedSearchGroupView | undefined,
  currentSiteId: string | undefined,
  ownLabel: string,
): IClientGroupSitePlacement => {
  const list: ISiteInfoUrl[] = sites ?? [];
  const listedById: Map<string, ISiteInfoUrl> = new Map<string, ISiteInfoUrl>();
  list.forEach(site => {
    const key: string = idKey(site?.id);
    if (key && !listedById.has(key)) {
      listedById.set(key, site);
    }
  });

  const currentKey: string = idKey(currentSiteId);
  const currentCollection: string | undefined = SharePointSiteHelper.extractSharePointSiteId(currentSiteId);
  const isCurrent = (id: string): boolean =>
    idKey(id) === currentKey || (!!currentCollection && SharePointSiteHelper.extractSharePointSiteId(id) === currentCollection);

  const placed: Set<string> = new Set<string>();
  const sections: IClientGroupSiteSection[] = [];
  const tickIds: string[] = [];
  const added: ISiteInfoUrl[] = [];

  const addSection = (key: string, text: string, groupSites: (IAdvancedSearchGroupSite | undefined)[]): void => {
    const sectionSites: ISiteInfoUrl[] = [];
    groupSites.forEach(groupSite => {
      const siteKey: string = idKey(groupSite?.id);
      if (!siteKey || placed.has(siteKey) || isCurrent(groupSite.id)) {
        return;
      }
      placed.add(siteKey);

      const listed: ISiteInfoUrl | undefined = listedById.get(siteKey);
      if (listed) {
        tickIds.push(listed.id);
        if (!listed.isSubsite && !listed.isChannel) {
          sectionSites.push(listed);
        }
        return;
      }

      // A site without a url cannot be searched, and every site in the list is matched by url
      // (the subsite rule) - one without would throw there and lose the whole initial selection.
      if (typeof groupSite.webUrl !== "string" || !groupSite.webUrl.trim()) {
        return;
      }
      const entry: ISiteInfoUrl = { id: groupSite.id, name: groupSite.displayName, webUrl: groupSite.webUrl, isSubsite: false, key: groupSite.id };
      added.push(entry);
      tickIds.push(entry.id);
      sectionSites.push(entry);
    });
    if (sectionSites.length > 0) {
      sections.push({ key, text, sites: sectionSites });
    }
  };

  addSection(CLIENT_GROUP_SECTION_KEY, ownLabel, view?.members ?? []);
  (view?.groups ?? []).forEach(group => {
    if (group) {
      addSection(`${CLIENT_GROUP_SECTION_KEY}:${group.itemId}`, group.label, [group.site, ...(group.members ?? [])]);
    }
  });

  return {
    sites: [...list, ...added],
    sections,
    tickIds: tickIds.length <= MAX_TICKED_GROUP_SITES ? tickIds : [],
    groupSiteIds: tickIds,
    added,
  };
};

/** The selection with the listed sites behind `tickIds` added after it, each site once. */
export const withTickedGroupSites = (selected: ISiteInfoUrl[], sites: ISiteInfoUrl[], tickIds: string[]): ISiteInfoUrl[] => {
  const selection: ISiteInfoUrl[] = (selected ?? []).slice();
  const chosen: Set<string> = new Set<string>();
  selection.forEach(site => chosen.add(idKey(site?.id)));
  (tickIds ?? []).forEach(id => {
    const key: string = idKey(id);
    if (!key || chosen.has(key)) {
      return;
    }
    const site: ISiteInfoUrl | undefined = (sites ?? []).find(candidate => idKey(candidate?.id) === key);
    if (site) {
      chosen.add(key);
      selection.push(site);
    }
  });
  return selection;
};

export interface IInitialSiteSelectionRequest {
  /** The picker's sites, group sites included. */
  sites: ISiteInfoUrl[];
  /** The list as it was before any group site was appended: offered when nothing else is ticked. */
  sitesBeforeGroups: ISiteInfoUrl[];
  /** The site the window was opened from, if any. */
  siteId: string | undefined;
  /** The remembered selection, where the tenant lets it be restored. */
  rememberedIds: string[] | undefined;
  /** The client group sites to tick with the site (placeClientGroupSites). */
  tickIds: string[];
}

/**
 * The sites ticked when the window opens: the remembered selection; the site it was opened from
 * with its subsites and channel sites, when the remembered selection lacks it; and that site's
 * client group sites (within the tick bound) on every open, remembered selection or not - they
 * are never remembered (rememberedSiteIds). With nothing ticked, every site there was before the
 * group sites were added.
 */
export const initialSiteSelection = ({ sites, sitesBeforeGroups, siteId, rememberedIds, tickIds }: IInitialSiteSelectionRequest): ISiteInfoUrl[] => {
  const list: ISiteInfoUrl[] = sites ?? [];
  let selection: ISiteInfoUrl[] = Array.isArray(rememberedIds) ? list.filter(site => rememberedIds.some(id => id === site.id)) : [];
  const requested: ISiteInfoUrl | undefined = siteId ? list.find(site => site.id === siteId) : undefined;
  if (requested) {
    if (selection.findIndex(site => site.id === siteId) === -1) {
      selection.push(requested);
      // Its sub and channel sites with it.
      list.filter(site => site.id !== requested.id && ((site.isChannel && site.teamSiteId === requested.id) || site.webUrl?.includes(requested.webUrl + "/")))
        .forEach(site => selection.push(site));
    }
    selection = withTickedGroupSites(selection, list, tickIds);
  }
  return selection.length > 0 ? selection : (sitesBeforeGroups ?? []);
};

/** The selection to remember, without this open's client group sites: those are ticked again,
 * from the site they belong to, and would otherwise follow the user to another client's search. */
export const rememberedSiteIds = (selectedIds: string[], groupSiteIds: string[] | undefined): string[] => {
  const groupIds: Set<string> = new Set<string>();
  (groupSiteIds ?? []).forEach(id => groupIds.add(idKey(id)));
  return (selectedIds ?? []).filter(id => !groupIds.has(idKey(id)));
};

/** The sites the Advanced Search cache does not hold yet. Libraries only load for cached sites,
 * and re-writing a cached site would wipe the libraries cached with it. */
export const sitesMissingFromCache = (candidates: ISiteInfoUrl[], cached: ISiteInfoUrl[] | undefined): ISiteInfoUrl[] => {
  const cachedIds: Set<string> = new Set<string>();
  (cached ?? []).forEach(site => cachedIds.add(idKey(site?.id)));
  return (candidates ?? []).filter(site => !cachedIds.has(idKey(site?.id)));
};

/** The sites the picker's Sites, Teams and Subsites sections show: every site not in a group section. */
export const sitesOutsideSections = (sites: ISiteInfoUrl[], sections: IClientGroupSiteSection[] | undefined): ISiteInfoUrl[] => {
  const inSections: Set<string> = new Set<string>();
  (sections ?? []).forEach(section => (section?.sites ?? []).forEach(site => inSections.add(idKey(site?.id))));
  return (sites ?? []).filter(site => !inSections.has(idKey(site?.id)));
};

/** The libraries that load views: all but those of the client group sites, whose libraries share
 * the current site's template and would only add reads and identical "All Documents" entries. */
export const librariesOutsideGroupSites = <T extends { siteId?: string }>(libraries: T[], groupSiteIds: string[] | undefined): T[] => {
  const groupIds: Set<string> = new Set<string>();
  (groupSiteIds ?? []).forEach(id => groupIds.add(idKey(id)));
  return (libraries ?? []).filter(library => !groupIds.has(idKey(library?.siteId)));
};
